import type {
  Audio, Onset, Pixel, Second, Signature, Source, Staff, Unit,
} from '@/data';
import { SourceType, StaffType } from '@/data';
import type { Image3DPageData, ImageLikeSource } from '@/data/sources';
import type Tab from '@/data/Tab';
import type { AudioFileFormat } from '@/formats/inputSourcesParsers/AudioParser';
import AudioParser, { generateUniqueId } from '@/formats/inputSourcesParsers/AudioParser';
import type { GenericSource } from '@/formats/inputSourcesParsers/GenericSourceParser';
import {
  convertSourceFormat, GenericSourceType, inferGenericSourceType, isChild,
} from '@/formats/inputSourcesParsers/GenericSourceParser';
import type {
  AudioImageFileFormat,
  FileFormat,
  Image3DFileFormat,
  ImageDimensions,
  ImageFileFormat,
} from '@/formats/inputSourcesParsers/SourceParser';
import SourceParser from '@/formats/inputSourcesParsers/SourceParser';
import type { TabFileFormat } from '@/formats/inputSourcesParsers/TabParser';
import TabParser from '@/formats/inputSourcesParsers/TabParser';
import type { PieceFormat, SourcesFormat } from '@/formats/PieceParser';
import StaffParser from '@/formats/StaffParser';
import type {
  AudioImageSyncFormat, AudioSyncFormat, Image3DPageFormat,
  Image3DSyncFormat, ImageSyncFormat,
  UnitCoupleFormat,
} from '@/formats/UnitParser';
import UnitParser from '@/formats/UnitParser';
import type { LyricsFileFormat } from '@/formats/inputSourcesParsers/LyricsParser';
import LyricsParser from '@/formats/inputSourcesParsers/LyricsParser';
import type { LyricsEntry } from '@/data/Lyrics';
import type Lyrics from '@/data/Lyrics';
import { AlphaTabService } from './AlphatabService';
import { ClientResponse } from './ClientResponse';
import type { IClient } from './IClient';
import { loadImageDimensions } from './imagesUtils';
import { LyricsService } from './LyricsService';

/**
 * Filter out an array of settles promises and log errors
 * @param result The output array of Promise.allSettled
 * @returns All successful results
 */
function filterSettled<T>(result: PromiseSettledResult<T>[]): T[] {
  return result.map((r) => {
    if (r.status === 'fulfilled') {
      return r.value;
    }
    // eslint-disable-next-line no-console
    console.error('Could not load source', r.reason);
    return null;
  }).filter((r) => !!r) as T[];
}

async function loadImage(
  client: IClient,
  format: ImageFileFormat | AudioImageFileFormat,
  id: string,
  syncFormat: ImageSyncFormat | AudioImageSyncFormat,
  pixelUnit: Unit<Pixel>,
  track?: Audio,
) {
  const imageUrl = client.sourceUrl(format.image, id).data;
  const dimensions = await loadImageDimensions(imageUrl);

  const staves = StaffParser.parseFormat(syncFormat.staffs, dimensions.height);

  return SourceParser
    .fromFormat(format, dimensions, pixelUnit, imageUrl, track, [{
      height: dimensions.height,
      width: dimensions.width,
      rows: [{
        staves,
        y: staves[0].top,
        height: staves[0].height,
        end: dimensions.width,
        start: 0 as Pixel,
      }],
    }]);
}

/**
 * Load an image source using info from the Piece object
 * @param this the dezrann client
 * @param format the format of the image info, from the SourceFormat
 * @param id the id of the piece
 * @returns a complete Source object
 */
export async function loadImageSource(
  this: IClient,
  format: ImageFileFormat,
  id: string,
): Promise<Source> {
  const url = this.sourceUrl(format.positions, id).data;

  const syncFormat = (await this.get<ImageSyncFormat>(url)).data;
  const pixelUnit = UnitParser.createImageUnit(syncFormat);
  return loadImage(
    this,
    format,
    id,
    syncFormat,
    pixelUnit,
  );
}

function pageToPageData(
  page : Image3DPageFormat,
  dimensions : ImageDimensions,
  resourcesUrl?:string,
)
  : Image3DPageData {
  const pageData: Image3DPageData = {
    image: `${resourcesUrl}${page.image}`,
    height: dimensions.height,
    width: dimensions.width,
    rows: [],
  };
  page.rows.forEach((row) => {
    const parsedStaves: Staff[] = row.staves?.map((s, index) => ({
      top: s.top as Pixel,
      bottom: s.bottom as Pixel,
      height: s.bottom - s.top as Pixel,
      id: index + 1,
      type: StaffType.staff,
      name: (index + 1).toString(),
    })) || [];
    pageData.rows.push({
      staves: [
        {
          top: row.y as Pixel,
          bottom: (row.y + row.height) as Pixel,
          height: row.height as Pixel,
          id: 0,
          type: StaffType.bar,
          name: 'all',
        },
        ...parsedStaves,
      ],
      y: row.y as Pixel,
      height: row.height as Pixel,
      end: row['end-x'] as Pixel,
      start: row['start-x'] as Pixel,
    });
  });
  return pageData;
}
export async function loadImage3DSource(
  this: IClient,
  format: Image3DFileFormat,
  id: string,
): Promise<Source> {
  const url = this.sourceUrl(format.positions, id).data;
  const imageUrl = this.buildUrl(id, format.image).data;
  const syncFormat = (await this.get<Image3DSyncFormat>(url)).data;
  const pixel3DUnit = UnitParser.createImage3DUnit(syncFormat);
  let resourcesUrl = '';
  if (this.isLocalClient) {
    resourcesUrl = this.buildUrl(`${id}/`).data;
  } else if (syncFormat.pages[0].image.endsWith('.svg')) {
    resourcesUrl = `${this.baseUrl}resources/${id}/sources/images/score/`;
  } else {
    resourcesUrl = `${this.baseUrl}resources/${id}/sources/images/scan/`;
  }
  const dimensions = await loadImageDimensions(`${resourcesUrl}${syncFormat.pages[0].image}`);
  const pages: Array<Image3DPageData> = [];
  // eslint-disable-next-line no-plusplus
  for (let pageIndex = 0; pageIndex < syncFormat.pages.length; pageIndex++) {
    const page = syncFormat.pages[pageIndex];
    // eslint-disable-next-line no-await-in-loop
    const dim = await loadImageDimensions(`${resourcesUrl}${page.image}`);
    pages.push(pageToPageData(page, dim, resourcesUrl));
  }

  return SourceParser
    .fromFormat(format, dimensions, pixel3DUnit, imageUrl, undefined, pages);
}

/**
 * Load an audio image source (spectrogram) using info from the Piece object
 * @param this the dezrann client
 * @param format the format of the audio image info, from the SourceFormat
 * @param secondUnit the onset-to-second unit of the parent track
 * @param id the id of the piece
 * @returns a complete Source object
 */
async function loadAudioImageSource(
  this: IClient,
  format: AudioImageFileFormat,
  track: Audio,
  id: string,
): Promise<Source> {
  const timeToPixelUrl = this.sourceUrl(format.positions, id).data;
  const timeToPixel = (await this.get<AudioImageSyncFormat>(timeToPixelUrl)).data;
  const pixelUnit = UnitParser.createAudioImageUnit(track.secondUnit, timeToPixel);
  return loadImage(
    this,
    format,
    id,
    timeToPixel,
    pixelUnit,
    track,
  );
}

/**
 * Load an audio track using info from the piece object
 * @param this The dezrann Client
 * @param format the format of the audio info, from the SourceFormat
 * @param unit the onset-to-second unit of the track, pre-loaded by loadAudioSources
 * @param id of the piece
 * @returns
 */
function loadAudio(
  this: IClient,
  format: AudioFileFormat,
  secondUnit: Unit<Second>,
  id: string,
): Audio {
  let url: string | undefined;
  let ytId: string | undefined;

  // Using the object structure, load the audio id from the backend or youtube
  if (format['yt-id' as keyof AudioFileFormat]) {
    ytId = format['yt-id' as keyof AudioFileFormat] as string;
  }
  if (format.file) {
    url = this.sourceUrl(format.file, id).data;
  }

  if (!ytId && !url) {
    throw ClientResponse.fromParsingError(new Error('Invalid Audio Format: missing source'));
  }

  return AudioParser.fromFormat(
    format,
    secondUnit,
    url,
    ytId,
    format.id,
  );
}

/**
 * Load a atb using info from the piece object
 * @param this The dezrann Client
 * @param format the format of the tab info
 * @param id of the piece
 * @returns
 */
async function loadTab(
  this: IClient,
  format: TabFileFormat,
  id: string,
  renderLinear = false,
): Promise<Tab> {
  if (!(format.tab)) {
    throw ClientResponse.fromParsingError(new Error('Invalid Tab Format: missing source'));
  }
  const url = this.sourceUrl(format.tab, id).data;
  const rawScore = (await this.get<ArrayBuffer>(url, 'arraybuffer')).data;

  const atService = new AlphaTabService(new Uint8Array(rawScore), this, renderLinear);

  await atService.loadAndRender();

  // const pixel3DUnit = UnitParser.createImage3DUnit(syncFormat);

  const pixel3DUnit = UnitParser.createImage3DUnit(atService.positions);
  const dim : ImageDimensions = atService.dimensions;
  // see https://stackoverflow.com/questions/5010288/how-to-make-a-function-wait-until-a-callback-has-been-called-using-node-js

  return TabParser.fromFormat(
    format,
    url,
    atService.svgImage,
    pixel3DUnit,
    dim,
    atService.positions.pages.map((page) => pageToPageData(page, dim)),
    renderLinear,
  );
}

async function loadLyrics(
  this: IClient,
  format: LyricsFileFormat,
  id: string,
  staves?: Staff[],
): Promise<Lyrics> {
  if (!(format.lyrics)) {
    throw ClientResponse.fromParsingError(new Error('Invalid Lyrics Format: missing source'));
  }
  const url = this.sourceUrl(format.lyrics, id).data;
  const rawData = (await this.get<{labels: LyricsEntry[]}>(url)).data;
  rawData.labels.sort((a, b) => (a.start as number) - (b.start as number));

  const lyricsService = new LyricsService(rawData.labels, staves);
  lyricsService.loadAndRender();

  const pixel3DUnit = UnitParser.createImage3DUnit(lyricsService.positions);
  const dim : ImageDimensions = lyricsService.dimensions;

  return LyricsParser.fromFormat(
    format,
    rawData.labels,
    pixel3DUnit,
    dim,
    lyricsService.positions.pages.map((page) => pageToPageData(page, dim)),
    lyricsService.renderRows,
    lyricsService.renderConfig,
  );
}

export interface AudioSources {
  track: Audio;
  images: Source[];
}
async function loadAudioSources(
  this: IClient,
  format: AudioFileFormat,
  id: string,
): Promise<Audio> {
  const audioSyncUrl = format['onset-date'];

  if (!audioSyncUrl) {
    throw ClientResponse.fromParsingError(new Error('Invalid Audio Format: no sync url'));
  }
  const onsetToTimeUrl = this.sourceUrl(audioSyncUrl, id).data;

  const onsetToTime = (await this.get<AudioSyncFormat>(onsetToTimeUrl)).data;

  const secondUnit = UnitParser.createAudioUnit(onsetToTime);

  const track = loadAudio.bind(this)(format, secondUnit, id);

  return track;
}

async function loadTabSources(
  this: IClient,
  format: TabFileFormat,
  id: string,
  renderLinear = false,
): Promise<Tab> {
  const tab = loadTab.bind(this)(format, id, renderLinear);

  return tab;
}

async function loadLyricsSources(
  this: IClient,
  format: LyricsFileFormat,
  id: string,
  staves?: Staff[],
): Promise<Lyrics> {
  const lyrics = loadLyrics.bind(this)(format, id, staves);
  return lyrics;
}

export interface PieceSources {
  sources: ImageLikeSource[];
  tracks: Audio[];
}

export async function loadPieceSourcesLegacy(
  this: IClient,
  format: PieceFormat,
  path: string,
): Promise<PieceSources> {
  /* 2026: Do not add new code here, add rather code in loadPieceSources */
  // force Id for audio sources
  const sourcesFormat : SourcesFormat = format.sources as SourcesFormat;
  sourcesFormat
    ?.audios
    ?.forEach((audio) => {
      if (!audio.id) {
      // just trying to make it unique
        // eslint-disable-next-line no-param-reassign
        audio.id = generateUniqueId(audio);
      }
    });
  // Get promises for all scores
  const scoreSourcePromises: Promise<Source>[] = sourcesFormat
    ?.images
    ?.map((imageFormat) => {
      if (imageFormat.type === SourceType.Image3D) {
        return loadImage3DSource.bind(this)(imageFormat, path);
      }
      return loadImageSource.bind(this)(imageFormat, path);
    })
    ?? [];

  // Get promises for all audio files
  const audioSourcePromises: Promise<Audio>[] = sourcesFormat
    ?.audios
    ?.map((audioFormat) => loadAudioSources.bind(this)(audioFormat, path))
    ?? [];

  // wait for audio source to be loaded
  const audioSourceSettled = await Promise.allSettled(audioSourcePromises);
  const tracks = filterSettled(audioSourceSettled);

  const audioImageSourcePromises: Promise<Source>[] = sourcesFormat
    ?.audios
    ?.flatMap((audioFormat) => {
      const correspondingTrack = tracks.find((track) => track.id === audioFormat.id);
      if (!correspondingTrack) {
        throw new Error(`Couldn't find track for id ${audioFormat.id}`);
      }
      return audioFormat.images?.map((imageFormat) => {
        const newFormat = imageFormat;
        newFormat.license = audioFormat.license ?? '';
        newFormat.info = audioFormat.info ?? '';
        return loadAudioImageSource.bind(this)(newFormat, correspondingTrack, path);
      })
      ?? [];
    })
    ?? [];

  const scoresSourcesSettled = await Promise.allSettled(scoreSourcePromises);
  const scoreSources = filterSettled(scoresSourcesSettled);

  const audioImageSourcesSettled = await Promise.allSettled(audioImageSourcePromises);
  const audioImageSources = filterSettled(audioImageSourcesSettled);

  const sources = [
    ...scoreSources,
    ...audioImageSources,
  ];

  sources.forEach((s, index) => s.setId(index));
  return { sources, tracks };
}

async function isPositionFile3d(imageFormat: FileFormat, client: IClient) : Promise<boolean> {
  const url = client.sourceUrl(imageFormat.positions).data;
  const syncFormat = (await client.get<Image3DSyncFormat>(url)).data;
  return syncFormat.pages !== undefined && syncFormat.pages[0].rows !== undefined;
}
async function loadAnyImageSource(this: IClient, format: FileFormat, path: string)
  : Promise<Source> {
  return isPositionFile3d(format, this).then((is3d) => {
    if (is3d) {
    // eslint-disable-next-line no-param-reassign
      format.type = SourceType.Image3D;
      return loadImage3DSource.bind(this)(format as Image3DFileFormat, path);
    }
    // eslint-disable-next-line no-param-reassign
    format.type = SourceType.Score;
    return loadImageSource.bind(this)(format as ImageFileFormat, path);
  });
}
export function convertAllFormats(sourcesJSON : Map<string, unknown>[]): GenericSource[] {
  return sourcesJSON.map((sourceJSON) => {
    const type : GenericSourceType = inferGenericSourceType(sourceJSON);
    const format : FileFormat |
    AudioFileFormat | TabFileFormat | undefined = convertSourceFormat(sourceJSON, type);
    return {
      type,
      format,
      parentId: sourceJSON.get('ref-source') as string,
    };
  })
    .filter((genericsource) => (typeof genericsource.format !== 'undefined'));
}
export function buildSourceMap(JSON : Record<string, unknown>) :Map<string, unknown> {
  const map = new Map<string, unknown>();
  Object.entries(JSON).forEach(([key, value]) => {
    map.set(key as string, value);
  });
  return map;
}
export async function loadPieceSources(
  this: IClient,
  format: PieceFormat,
  path: string,
): Promise<PieceSources> {
  const rawSourceFormats = format.sources as Record<string, unknown>[];
  const sourceFormats = (rawSourceFormats as Record<string, unknown>[]).map(
    (s) => buildSourceMap(s),
  );
  const genericSources = convertAllFormats(sourceFormats);

  // Get promises for all scores
  const scoreSourcePromises: Promise<Source>[] = genericSources
    .filter((source) => !isChild(source) && source.type === GenericSourceType.Image)
    ?.map(
      async (imageSource) => loadAnyImageSource.bind(this)(imageSource.format as FileFormat, path),
    )
    ?? [];

  // Get promises for all audio files
  const audioSourcePromises: Promise<Audio>[] = genericSources
    .filter((source) => !isChild(source) && source.type === GenericSourceType.Audio)
    ?.map((audioSource) => loadAudioSources.bind(this)(audioSource.format as AudioFileFormat, path))
    ?? [];

  const tabSourcePromises: Promise<Tab>[] = genericSources
    .filter((source) => !isChild(source) && source.type === GenericSourceType.Tab)
    ?.map(
      (tabSource) => [
        loadTabSources.bind(this)(tabSource.format as TabFileFormat, path, true),
        loadTabSources.bind(this)(tabSource.format as TabFileFormat, path, false),
      ],

    ).flat()
    ?? [];

  // wait for audio source to be loaded
  const audioSourceSettled = await Promise.allSettled(audioSourcePromises);
  const parentTracks = filterSettled(audioSourceSettled);

  const scoresSourcesSettled = await Promise.allSettled(scoreSourcePromises);
  const scoreSources = filterSettled(scoresSourcesSettled);

  const tabSourcesSettled = await Promise.allSettled(tabSourcePromises);
  const tabSources = filterSettled(tabSourcesSettled);

  const sourceStaves = scoreSources.length > 0 && scoreSources[0].pages
    ? scoreSources[0].pages[0]?.rows[0]?.staves.filter((s) => s.name !== 'all')
    : undefined;

  const lyricsSourcePromises: Promise<Lyrics>[] = genericSources
    .filter((source) => !isChild(source) && source.type === GenericSourceType.Lyrics)
    ?.map((lyricsSource) => loadLyricsSources.bind(this)(
      lyricsSource.format as LyricsFileFormat,
      path,
      sourceStaves,
    ))
    ?? [];

  const lyricsSourcesSettled = await Promise.allSettled(lyricsSourcePromises);
  const lyricsSources = filterSettled(lyricsSourcesSettled);

  const audioImageSourcePromises: Promise<Source>[] = genericSources
    .filter((source) => isChild(source) && source.type === GenericSourceType.Image)
    ?.flatMap((childSource) => {
      const parentSource = parentTracks.find((track) => track.id === childSource.parentId);
      if (!parentSource) { // parent source is not a track
        return loadAnyImageSource.bind(this)(childSource.format as FileFormat, path);
      }

      const parentFormat = genericSources.find(
        (source) => source.format?.id === childSource.parentId,
      )?.format;

      const newFormat = childSource.format! as FileFormat;
      newFormat.type = SourceType.AudioImage;
      if (typeof parentFormat !== 'undefined') {
        newFormat.license = parentFormat.license ?? '';
        newFormat.info = parentFormat.info ?? '';
      }
      return loadAudioImageSource.bind(this)(
        newFormat as AudioImageFileFormat,
        parentSource,
        path,
      );
    })
      ?? [];

  const audioChildrenSourcePromises: Promise<Audio>[] = genericSources
    .filter((source) => isChild(source) && source.type === GenericSourceType.Audio)
    ?.map((audioFormat) => loadAudioSources.bind(this)(audioFormat.format as AudioFileFormat, path))
    ?? [];

  const audioImageSourcesSettled = await Promise.allSettled(audioImageSourcePromises);
  const audioImageSources = filterSettled(audioImageSourcesSettled);

  const childrenAudioSourceSettled = await Promise.allSettled(audioChildrenSourcePromises);
  const childrenTracks = filterSettled(childrenAudioSourceSettled);

  const sources = [
    ...scoreSources,
    ...audioImageSources,
    ...tabSources,
    ...lyricsSources,
  ];

  const tracks = [
    ...parentTracks,
    ...childrenTracks,
  ];

  sources.forEach((s, index) => s.setId(index));

  return { sources, tracks };
}

export function generateGridSource(
  signature: Signature,
  measureCount: number,
  measuresPerRow: number,
  breakPoints: Array<Onset>,
  pageBreakPoints: Array<Onset>,
  gridLines: number,
): Source {
  // Grid lines heights
  const TOP_BOT_Y: Record<number, number[]> = {
    0: [10, 15, 20, 25, 30, 60, 90, 100], // as before, bot.2 + bot.3
    1: [0, 2, 5, 95, 96, 98, 99, 100], // top.3
    2: [0, 5, 55, 105, 108, 109, 110], // top.2 + top.3
    3: [5, 55, 105, 155, 158, 159, 160], // top.1 + top.2 + top.3
  };

  const topbotY = TOP_BOT_Y[gridLines];
  const gridLinesNames = ['top.1', 'top.2', 'top.3', 'bot.1', 'bot.2', 'bot.3'];

  const leftMargin = 100;
  const rowWidth = 1600;
  const onsetsPerRow = measuresPerRow * 4;
  const onsetWidth = rowWidth / onsetsPerRow;
  const cellHeight = topbotY.at(-1) ?? 100;
  const pageMargin = 10;

  const unitPages: Image3DSyncFormat = {
    pages: [{
      image: '',
      rows: [],
    }],
  };
  const imagePages: Image3DPageData[] = [{
    height: pageMargin,
    width: rowWidth,
    rows: [],
  }];

  let currentPage = 0;
  // let currentRow = 0;
  let currentRowInPage = 0;

  const firstCompleteMeasure = signature.hasUpbeat ? 2 : 1;
  let firstOnsetOfRow = signature.getOnsetFromMeasure(firstCompleteMeasure);
  // let currentMeasureInRow = 0;
  let currentBreakpoint = 0;
  const currentPageBreakpoint = 0;
  let y = 0;

  let onsetX: UnitCoupleFormat<'onset' | 'x'> = [];

  // This loop is on measures, we do not want to break measures
  for (let m = 1; m <= measureCount; m += 1) {
    // TODO: #628, non 4/4, from mmap
    const currentOnset = signature.getOnsetFromMeasure(m);
    const currentMeasureDuration = signature.getMeasureDuration();

    // CHECK: should becurrentRowInPage, not currentRow?
    y = (cellHeight * currentRowInPage) + (pageMargin * currentPage);

    // Starts new page ? new row ?
    const newPage = (currentOnset >= pageBreakPoints[currentPageBreakpoint]);

    let newRow = newPage
    || (currentOnset + currentMeasureDuration - firstOnsetOfRow > onsetsPerRow); // Row is full
    // || (currentMeasureInRow >= measuresPerRow); // Row is full

    if (currentOnset >= breakPoints[currentBreakpoint]) {
      newRow = true;
      currentBreakpoint += 1;
    }

    // || Row break... #636
    const lastRow = (m === measureCount); // Flush last row;

    // Push previous row (when it exists)
    if ((newRow || lastRow) && onsetX.length) {
      unitPages.pages[currentPage].rows.push({
        'onset-x': onsetX,
        'start-x': leftMargin,
        'end-x': leftMargin + rowWidth,
        y,
        height: 0,
      });

      imagePages[currentPage].height += cellHeight;

      // Creates staves
      const staves = [];
      staves.push({
        id: 0,
        top: y + topbotY[0] as Pixel,
        bottom: y + topbotY[topbotY.length - 1] as Pixel,
        height: (topbotY[topbotY.length - 1] - topbotY[0]) as Pixel,
        name: 'all',
        type: StaffType.line,
      });

      for (let i = 0; i < gridLinesNames.length; i += 1) {
        const top = topbotY[i];
        const bottom = topbotY[i + 1];
        staves.push({
          id: i + 1,
          top: y + top as Pixel,
          bottom: y + bottom as Pixel,
          height: (bottom - top) as Pixel,
          name: gridLinesNames[i],
          type: StaffType.line,
        });
      }

      imagePages[currentPage].rows.push({
        start: leftMargin as Pixel,
        end: leftMargin + rowWidth as Pixel,
        y: y as Pixel,
        height: cellHeight as Pixel,
        staves,
      });
    }

    if (lastRow) break;

    if (newRow) {
      // currentRow += 1;
      currentRowInPage += 1;
      // currentMeasureInRow = 0;
      firstOnsetOfRow = currentOnset;
      onsetX = [];
    }

    // Push previous page
    if (newPage) {
      currentBreakpoint += 1;
      currentPage += 1;
      currentRowInPage = 0;

      unitPages.pages.push({
        image: '',
        rows: [],
      });
      imagePages.push({
        height: pageMargin,
        width: rowWidth,
        rows: [],
      });
    }

    // Now we push the onsets of this measure
    for (let x = 0; x <= currentMeasureDuration; x += 1) {
      onsetX.push({
        onset: currentOnset + x,
        x: leftMargin + (currentOnset + x - firstOnsetOfRow) * onsetWidth,
      });
    }
    // currentMeasureInRow += 1;
  } // end loop on measures

  const source = SourceParser.fromFormat({
    image: '',
    positions: '',
    type: SourceType.Grid,
  }, {
    // TODO: What height should we put here? Combined pages? One page?
    height: y as Pixel,
    width: leftMargin + rowWidth as Pixel,
  }, UnitParser.createImage3DUnit(unitPages), '', undefined, imagePages);
  return source;
}
